import { useEffect, useRef, useState } from 'react';
import Localize from 'react-intl-universal';

import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import StopIcon from '@mui/icons-material/Stop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { generateResourceTypeFromResponse, isOrbis } from '@common/Util';
import { getByPathAndParams } from '@services/BaseApi';

const AudioPreview = ({ url, name, isCatalog = false }) => {
  const [audioBlob, setAudioBlob] = useState(null);
  const [isAudioLoading, setIsAudioLoading] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const buttonRef = useRef(null);

  const catalogAudioPlayHandler = (event) => {
    event.stopPropagation();
    setIsPlaying(true);
    fetchAudio();
  };

  const handlePlayStop = (event) => {
    event.stopPropagation();
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const fetchAudio = async () => {
    if (audioBlob) {
      return;
    }
    try {
      setIsAudioLoading(true);
      await getByPathAndParams({
        path: url,
        responseType: 'arraybuffer',
        temApi: true
      })
        .then((response) => {
          const audioUrl = URL.createObjectURL(
            new Blob([response?.data], { type: generateResourceTypeFromResponse(response) })
          );
          setAudioBlob(audioUrl);
          setIsAudioLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching audio:', error);
          setIsAudioLoading(false);
        });
    } catch (error) {
      console.error('Error fetching audio:', error);
    }
  };

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [isAudioLoading, audioBlob, isPlaying]);

  useEffect(() => {
    return () => {
      if (audioBlob) {
        URL.revokeObjectURL(audioBlob);
      }
    };
  }, [audioBlob]);

  if (!url || !isOrbis()) {
    return null;
  }

  if (isCatalog) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '40px',
          height: '40px',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: '50%',
          color: 'primary.main'
        }}
      >
        {isAudioLoading ? (
          <CircularProgress size={25} role='status' aria-label={Localize.get('App.LoadingAudio')} />
        ) : !audioBlob ? (
          <IconButton
            onClick={catalogAudioPlayHandler}
            aria-label={Localize.get('App.PlayAudioSample')}
          >
            <RecordVoiceOverIcon sx={{ fontSize: '1.5rem', color: 'primary.main' }} />
          </IconButton>
        ) : (
          <>
            <IconButton
              ref={buttonRef}
              onClick={handlePlayStop}
              aria-label={
                isPlaying ? Localize.get('App.StopAudioPlayback') : Localize.get('App.PlayAudio')
              }
            >
              {isPlaying ? (
                <StopIcon sx={{ color: 'primary.main' }} />
              ) : (
                <PlayArrowIcon sx={{ color: 'primary.main' }} />
              )}
            </IconButton>
            <audio
              ref={audioRef}
              src={audioBlob}
              aria-live='polite'
              style={{ display: 'none' }}
              autoPlay
              onEnded={() => setIsPlaying(false)}
            >
              <track kind='captions' />
            </audio>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginLeft: { xs: 0, sm: '1rem' },
        flex: 1,
        minWidth: { xs: '100%', sm: '200px' }
      }}
    >
      {audioBlob ? (
        <audio
          ref={buttonRef}
          controls
          autoPlay
          src={audioBlob}
          style={{ maxWidth: '100%', margin: '0 10px', maxHeight: '30px' }}
          aria-label={name || Localize.get('App.PlayAudioDescription')}
        >
          <track kind='captions' />
        </audio>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.7rem',
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Button
            variant='outlined'
            onClick={fetchAudio}
            sx={{
              padding: '0',
              width: '30px',
              border: 'none',
              '&:hover': { border: 'none' }
            }}
            aria-label={
              isAudioLoading ? Localize.get('App.LoadingAudio') : Localize.get('App.DownloadAudio')
            }
          >
            {isAudioLoading ? (
              <CircularProgress
                size={25}
                role='status'
                aria-label={Localize.get('App.LoadingAudio')}
              />
            ) : (
              <CloudDownloadIcon fontSize='large' />
            )}
          </Button>
          <Tooltip title={name ? name : `${Localize.get('App.PlayAudioDescription')}`} arrow>
            <Typography
              noWrap
              aria-hidden='true'
              variant='body'
              component='div'
              sx={{
                lineHeight: '30px'
              }}
            >
              {Localize.get('App.PlayAudioDescription')}
            </Typography>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default AudioPreview;
